import { useEffect, useState, useRef, useMemo } from 'react';
import {
  Map,
  Marker,
  MapRef,
  FullscreenControl,
  NavigationControl,
  Popup,
} from 'react-map-gl';
import { toast } from 'react-toastify';

import { META_COLLECT_API } from '../../api';
import { ReactComponent as TankIcon } from '../../assets/TankItem/TankPin.svg';
import { useSigninContext } from '../../context/AuthProviderPages/Signin';
import { MapContainer } from '../../styles/global.styles';
import * as S from './styles';

interface ITank {
  DFIDTANQUE: number;
  DFDESCTANQUE: string;
  DFIDLINHA: number;
  DFNOMELINHA: string;
  DFATIVO: 'S' | 'N';
  DFCOLETASELETIVA: 'S' | 'N';
  DFLOCALIZACAO: {
    type: string;
    coordinates: [number, number]; // [longitude, latitude]
  };
}

interface IGeneralMap {
  lineIds?: number[];
  height?: string;
  lineColors?: { [key: number]: string };
  showInactiveLineIds?: number[];
}

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

export const GeneralMap = ({
  lineIds = [],
  height = '400px',
  lineColors = {},
  showInactiveLineIds = [],
}: IGeneralMap) => {
  const [tanks, setTanks] = useState<ITank[]>([]);
  const [selectedTank, setSelectedTank] = useState<ITank | null>(null);
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { path } = useSigninContext();

  // Força o redimensionamento do mapa quando o container mudar de tamanho
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      mapRef.current?.resize();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  // Processa os tanques para exibição com todos os atributos necessários
  const processedTanks = useMemo(() => {
    return tanks
      .filter(tank => {
        if (!tank.DFLOCALIZACAO?.coordinates) return false;

        const shouldShowInactive = showInactiveLineIds.includes(
          Number(tank.DFIDLINHA),
        );
        return tank.DFATIVO === 'S' || shouldShowInactive;
      })
      .map(tank => {
        const lineId = Number(tank.DFIDLINHA);
        return {
          ...tank,
          resolvedColor: lineColors[lineId] || '#007cbf',
          isActive: tank.DFATIVO === 'S',
        };
      });
  }, [tanks, showInactiveLineIds, lineColors]);

  useEffect(() => {
    if (path !== '') {
      if (lineIds.length === 0) {
        setTanks([]);
        setSelectedTank(null);
        return;
      }

      META_COLLECT_API.get('/tanque/mapa', {
        params: { DFIDLINHAS: lineIds },
        headers: { path },
      })
        .then(({ data }) => {
          const results: ITank[] = data.tanques || [];
          setTanks(results);

          const coords = results
            .filter(
              (t: ITank) => t.DFLOCALIZACAO && t.DFLOCALIZACAO.coordinates,
            )
            .map((t: ITank) => t.DFLOCALIZACAO.coordinates);

          if (coords.length > 0) {
            const lngs = coords.map(c => c[0]);
            const lats = coords.map(c => c[1]);

            mapRef.current?.fitBounds(
              [
                [Math.min(...lngs), Math.min(...lats)],
                [Math.max(...lngs), Math.max(...lats)],
              ],
              { padding: 70, duration: 2000, maxZoom: 15 },
            );
          }
        })
        .catch(() => toast.error('Falha ao carregar tanques'));
    }
  }, [path, lineIds]);

  const selectedProcessedTank = useMemo(() => {
    if (!selectedTank) return null;
    return processedTanks.find(t => t.DFIDTANQUE === selectedTank.DFIDTANQUE);
  }, [selectedTank, processedTanks]);

  return (
    <MapContainer
      ref={containerRef}
      style={{
        height,
        padding: 0,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: -46.6333,
          latitude: -23.5505,
          zoom: 10,
        }}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        style={{ width: '100%', height: '100%' }}
      >
        <FullscreenControl position="top-left" />
        <NavigationControl position="top-left" />

        {processedTanks.map(tank => (
          <Marker
            key={tank.DFIDTANQUE}
            longitude={tank.DFLOCALIZACAO.coordinates[0]}
            latitude={tank.DFLOCALIZACAO.coordinates[1]}
            anchor="bottom"
            onClick={e => {
              e.originalEvent.stopPropagation();
              setSelectedTank(tank);
            }}
          >
            <S.MarkerWrapper>
              <TankIcon
                width={35}
                height={45}
                fill={tank.resolvedColor}
                style={{
                  color: tank.resolvedColor,
                  filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.3))',
                  opacity: tank.isActive ? 1 : 0.6,
                }}
              />
              <S.MarkerLabel
                color={tank.resolvedColor}
                isActive={tank.isActive}
              >
                {tank.DFIDTANQUE}
              </S.MarkerLabel>
            </S.MarkerWrapper>
          </Marker>
        ))}

        {selectedProcessedTank && (
          <Popup
            anchor="top"
            longitude={selectedProcessedTank.DFLOCALIZACAO.coordinates[0]}
            latitude={selectedProcessedTank.DFLOCALIZACAO.coordinates[1]}
            onClose={() => setSelectedTank(null)}
            maxWidth="300px"
          >
            <S.PopupContainer>
              <S.PopupHeader>
                <strong>{selectedProcessedTank.DFDESCTANQUE}</strong>
                <S.StatusBadge isActive={selectedProcessedTank.isActive}>
                  {selectedProcessedTank.isActive ? 'ATIVO' : 'INATIVO'}
                </S.StatusBadge>
              </S.PopupHeader>

              <S.LineName color={selectedProcessedTank.resolvedColor}>
                {selectedProcessedTank.DFNOMELINHA}
              </S.LineName>

              <S.InfoBox>
                <div>
                  <strong>Coleta Seletiva:</strong>{' '}
                  {selectedProcessedTank.DFCOLETASELETIVA === 'S'
                    ? 'Sim'
                    : 'Não'}
                </div>

                <S.GoogleMapsLink
                  href={`https://www.google.com/maps/search/?api=1&query=${selectedProcessedTank.DFLOCALIZACAO.coordinates[1]},${selectedProcessedTank.DFLOCALIZACAO.coordinates[0]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google Maps
                </S.GoogleMapsLink>
              </S.InfoBox>
            </S.PopupContainer>
          </Popup>
        )}
      </Map>
    </MapContainer>
  );
};

export default GeneralMap;
